import { useEffect, useState } from "react"
import { Box, HStack, NativeSelect, Stack, Table } from "@chakra-ui/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Input, PaginationControls, Text, toaster } from "@/components"
import { usePagination } from "@/hooks"
import { createFmv, getFmvTypes } from "@/api"
import { CompanyFmv } from "@/types"

interface Props {
  companyId: number
  fmvs: CompanyFmv[]
}

const ITEMS_PER_PAGE = 50

/** Human-readable FMV type labels */
function formatTypeName(name: string | null | undefined): string {
  if (!name) return "Unknown"
  if (name.toLowerCase() === "409a") return "409A"
  if (name.toLowerCase() === "m&a") return "M&A"
  return name.charAt(0).toUpperCase() + name.slice(1)
}

export const CompanyFmvs = ({ companyId, fmvs }: Props) => {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fmvDate, setFmvDate] = useState("")
  const [fmvValue, setFmvValue] = useState("")
  const [fmvTypeId, setFmvTypeId] = useState("")
  const [fmvName, setFmvName] = useState("")
  const [fmvComment, setFmvComment] = useState("")
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null)

  const { data: fmvTypes = [] } = useQuery({
    queryKey: ["fmvTypes"],
    queryFn: getFmvTypes,
    staleTime: 1000 * 60 * 30,
  })

  // Default to first type once loaded
  useEffect(() => {
    if (fmvTypes.length > 0 && !fmvTypeId) {
      setFmvTypeId(String(fmvTypes[0].id))
    }
  }, [fmvTypes, fmvTypeId])

  const resetForm = () => {
    setFmvDate("")
    setFmvValue("")
    setFmvTypeId(fmvTypes.length > 0 ? String(fmvTypes[0].id) : "")
    setFmvName("")
    setFmvComment("")
    setShowForm(false)
  }

  const handleCreateFmv = async () => {
    if (!fmvDate) {
      toaster.create({ title: "Date is required", type: "error" })
      return
    }
    setIsSubmitting(true)
    try {
      await createFmv(companyId, {
        fmvTypeId: Number(fmvTypeId),
        value: fmvValue ? Number(fmvValue) : null,
        comment: fmvComment || null,
        name: fmvName || null,
        date: fmvDate,
      })
      toaster.create({ title: "FMV created", type: "success" })
      queryClient.invalidateQueries({ queryKey: ["companyInfo", companyId] })
      resetForm()
    } catch (error) {
      console.error("Error creating FMV:", error)
      toaster.create({ title: "Failed to create FMV", type: "error" })
    } finally {
      setIsSubmitting(false)
    }
  }

  // All FMVs with value and date, sorted by date descending
  const allFmvs = fmvs
    .filter((fmv) => fmv.value && fmv.date)
    .sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime())

  // Apply type filter for the table display
  const filteredFmvs = selectedTypeFilter !== null
    ? allFmvs.filter((fmv) => fmv.fmvTypeId === selectedTypeFilter)
    : allFmvs

  // Build counts per type for filter badges (from unfiltered list)
  const typeCounts = new Map<number, number>()
  for (const fmv of allFmvs) {
    typeCounts.set(fmv.fmvTypeId, (typeCounts.get(fmv.fmvTypeId) ?? 0) + 1)
  }
  // Only show filter buttons for types that exist in this company's FMV data
  const availableTypes = fmvTypes.filter((t) => typeCounts.has(t.id))

  // Standard FMVs (type 1) for the KPI header
  const standardFmvs = allFmvs.filter((fmv) => fmv.fmvTypeId === 1)
  const latestFmv = standardFmvs[0]

  // Find the next FMV with a different value than the latest
  const previousFmv = latestFmv
    ? standardFmvs.find((fmv) => fmv.value !== latestFmv.value)
    : undefined

  const calculatePercentChange = () => {
    if (!latestFmv || !previousFmv) return null
    const latest = parseFloat(latestFmv.value!)
    const previous = parseFloat(previousFmv.value!)
    if (previous === 0) return null
    return ((latest - previous) / previous) * 100
  }

  const percentChange = calculatePercentChange()

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const formatValue = (value: string) => {
    const num = parseFloat(value)
    return isNaN(num) ? value : `$${num.toFixed(2)}`
  }

  const { paginatedData: paginatedFmvs, currentPage, totalPages, startIndex, endIndex, setPage, getPageNumbers } = usePagination(filteredFmvs, ITEMS_PER_PAGE)

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={6} gap={8}>
        <Stack>
          <Text fontSize="xs" color="text.secondary">
            Current FMV
          </Text>
          <Text fontSize="3xl" fontWeight="bold" color="text.primary">
            {latestFmv ? formatValue(latestFmv.value!) : "N/A"}
          </Text>
          {latestFmv && (
            <Text fontSize="xs" color="text.muted">
              as of {formatDate(latestFmv.date!)}
            </Text>
          )}
        </Stack>

        <Stack>
          <Text fontSize="xs" color="text.secondary">
            Change from Previous
          </Text>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color={percentChange === null ? "gray.400" : percentChange >= 0 ? "green.500" : "red.500"}
          >
            {percentChange !== null ? `${percentChange >= 0 ? "+" : ""}${percentChange.toFixed(1)}%` : "N/A"}
          </Text>
          {previousFmv && (
            <Text fontSize="xs" color="text.muted">
              vs {formatValue(previousFmv.value!)} on {formatDate(previousFmv.date!)}
            </Text>
          )}
        </Stack>
      </HStack>

      <HStack mb={3} justifyContent="space-between">
        <Text fontSize="lg" fontWeight="bold">
          FMV History ({selectedTypeFilter !== null ? `${filteredFmvs.length} of ${allFmvs.length}` : allFmvs.length})
        </Text>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            + Add FMV
          </Button>
        )}
      </HStack>

      {availableTypes.length > 1 && (
        <HStack mb={4} gap={2} flexWrap="wrap">
          <Button
            size="xs"
            variant={selectedTypeFilter === null ? "solid" : "outline"}
            onClick={() => { setSelectedTypeFilter(null); setPage(1) }}
          >
            All
          </Button>
          {availableTypes.map((type) => (
            <Button
              key={type.id}
              size="xs"
              variant={selectedTypeFilter === type.id ? "solid" : "outline"}
              onClick={() => { setSelectedTypeFilter(selectedTypeFilter === type.id ? null : type.id); setPage(1) }}
            >
              {formatTypeName(type.name)} ({typeCounts.get(type.id) ?? 0})
            </Button>
          ))}
        </HStack>
      )}

      {showForm && (
        <Box mb={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="bg.subtle">
          <Text fontSize="sm" fontWeight="bold" mb={3}>New FMV</Text>
          <HStack gap={3} mb={3} flexWrap="wrap">
            <Stack gap={1} flex="1" minW="140px">
              <Text fontSize="xs" color="text.secondary">Date *</Text>
              <Input type="date" size="sm" value={fmvDate} onChange={(e) => setFmvDate(e.target.value)} />
            </Stack>
            <Stack gap={1} flex="1" minW="120px">
              <Text fontSize="xs" color="text.secondary">Value ($)</Text>
              <Input type="number" size="sm" placeholder="0.00" step="0.01" value={fmvValue} onChange={(e) => setFmvValue(e.target.value)} />
            </Stack>
            <Stack gap={1} flex="1" minW="120px">
              <Text fontSize="xs" color="text.secondary">Type</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field value={fmvTypeId} onChange={(e) => setFmvTypeId(e.target.value)}>
                  {fmvTypes.map((type) => (
                    <option key={type.id} value={String(type.id)}>
                      {formatTypeName(type.name)}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </Stack>
            <Stack gap={1} flex="1" minW="140px">
              <Text fontSize="xs" color="text.secondary">Name</Text>
              <Input size="sm" placeholder="e.g. 409A" value={fmvName} onChange={(e) => setFmvName(e.target.value)} />
            </Stack>
          </HStack>
          <Stack gap={1} mb={3}>
            <Text fontSize="xs" color="text.secondary">Comment</Text>
            <Input size="sm" placeholder="Optional comment" value={fmvComment} onChange={(e) => setFmvComment(e.target.value)} />
          </Stack>
          <HStack gap={2}>
            <Button size="sm" onClick={handleCreateFmv} loading={isSubmitting}>
              Create FMV
            </Button>
            <Button size="sm" variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </HStack>
        </Box>
      )}

      {filteredFmvs.length === 0 ? (
        <Text color="text.muted">
          {selectedTypeFilter !== null ? "No FMV records match the selected filter." : "No FMV records found for this company."}
        </Text>
      ) : (
        <>
          <Table.Root size="sm" striped css={{ "& tr:nth-of-type(odd) td": { bg: "table.stripe" } }}>
            <Table.Header>
              <Table.Row bg="table.row">
                <Table.ColumnHeader>Date</Table.ColumnHeader>
                <Table.ColumnHeader>Type</Table.ColumnHeader>
                <Table.ColumnHeader>Value</Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Comment</Table.ColumnHeader>
                <Table.ColumnHeader>Modified</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedFmvs.map((fmv) => (
                <Table.Row key={fmv.id} bg="table.row">
                  <Table.Cell>{formatDate(fmv.date!)}</Table.Cell>
                  <Table.Cell>
                    {fmv.companyFmvType?.name ? (
                      <Text fontSize="2xs" fontWeight="bold" bg="blue.50" color="blue.600" px={2} py={0.5} borderRadius="md" w="fit-content" _dark={{ bg: "blue.900", color: "blue.200" }}>
                        {formatTypeName(fmv.companyFmvType.name)}
                      </Text>
                    ) : fmv.fmvTypeId}
                  </Table.Cell>
                  <Table.Cell>{formatValue(fmv.value!)}</Table.Cell>
                  <Table.Cell>{fmv.name}</Table.Cell>
                  <Table.Cell>{fmv.comment}</Table.Cell>
                  <Table.Cell>{formatDate(fmv.updatedAt)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredFmvs.length}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setPage}
            getPageNumbers={getPageNumbers}
          />
        </>
      )}
    </Box>
  )
}
